import { Box, Skeleton } from '@chakra-ui/react';
import { Library } from '@googlemaps/js-api-loader';
import { useJsApiLoader } from '@react-google-maps/api';
import { ComboboxItem, ComboboxTagInput, ComboboxTagInputProps } from '@src/components/sco/ComboboxTagInput';
import { useDebounce } from '@uidotdev/usehooks';
import { useState, useEffect, useRef } from 'react';
import { LuMapPin } from 'react-icons/lu';
import PlacesService = google.maps.places.PlacesService;
import AutocompleteService = google.maps.places.AutocompleteService;
type LocationInputProps = {
  placeholder?: string;
  defaultValue?: LocationInputPlace;
  comboboxTagInputProps?: Partial<ComboboxTagInputProps>;
  onClear?: () => void;
  onChange?: (place: LocationInputPlace) => void;
  onInputChange?: (value: string) => void;
  placeTypes?: Array<'(cities)' // Cities only
  | '(regions)' // Regions/States
  | 'establishment' // Businesses/Points of Interest
  | 'address' // Street addresses
  | 'geocode' // All geographical locations
  | '(postal_code)' // Postal codes
  | 'country' // Countries
  | 'locality' // Cities/Towns
  | 'sublocality' // Districts/Subdivisions of cities
  | 'administrative_area_level_1' // States/Provinces
  | 'administrative_area_level_2' // Counties/Districts
  | 'train_station' // Train stations
  | 'transit_station' // Transit stations
  | 'airport' // Airports
  | 'bus_station' // Bus stations
  | 'subway_station' // Subway stations
  >;
};
export type LocationInputPlace = {
  city?: string;
  country?: string;
  countryISO?: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  placeTypes?: string[];
  postalCode?: string;
  state?: string;
  street?: string;
  title: string;
};
type LocationPrediction = {
  description: string;
  placeId: string;
};
const googleLibraries: Library[] = ['places', 'geocoding'];
export const LocationInput = (props: LocationInputProps) => {
  // Google Maps Setup
  const placesRef = useRef<HTMLDivElement>(null);
  const placesService = useRef<PlacesService>(null);
  const autocompleteService = useRef<AutocompleteService>(null);
  const {
    isLoaded
  } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    libraries: googleLibraries
  });
  const [predictionsLoading, setPredictionsLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(props.defaultValue?.title || '');
  const [selectedItems, setSelectedItems] = useState<ComboboxItem[]>(props.defaultValue ? [{
    key: 'default',
    ...props.defaultValue
  }] : []);
  const [place, setPlace] = useState<LocationInputPlace>(props.defaultValue);
  const [locationPredictions, setLocationPredictions] = useState<LocationPrediction[]>(props.defaultValue ? [{
    placeId: 'default',
    description: props.defaultValue.title,
    ...props.defaultValue
  }] : []);
  const debouncedInputValue = useDebounce(inputValue, 100);
  useEffect(() => {
    if (isLoaded && placesRef.current) {
      placesService.current = new google.maps.places.PlacesService(placesRef.current);
      autocompleteService.current = new google.maps.places.AutocompleteService();
    }
  }, [isLoaded, placesRef.current]);
  useEffect(() => {
    if (props.onInputChange) {
      props.onInputChange(inputValue);
    }
  }, [inputValue]);
  useEffect(() => {
    if (props.onChange) {
      props.onChange(place);
    }
  }, [place]);
  useEffect(() => {
    const newPlace = selectedItems[0] as LocationInputPlace;
    if (newPlace && newPlace.title == props.defaultValue?.title) {
      return;
    }
    if (newPlace && placesService.current) {
      placesService.current.getDetails({
        placeId: selectedItems[0].key,
        fields: ['geometry', 'formatted_address', 'place_id', 'address_components']
      }, (place_0, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place_0 && place_0.geometry) {
          // Helper function to find address components by type
          const getAddressComponent = (type: string, shortName: boolean = false) => {
            const component_0 = place_0.address_components?.find(component => component.types.includes(type));
            return component_0 ? shortName ? component_0.short_name : component_0.long_name : '';
          };
          setPlace({
            placeId: place_0.place_id!,
            title: place_0.formatted_address!,
            latitude: place_0.geometry.location!.lat(),
            longitude: place_0.geometry.location!.lng(),
            street: `${getAddressComponent('street_number') ? getAddressComponent('street_number') + ' ' : ''}${getAddressComponent('route')}`.trim(),
            city: getAddressComponent('locality') || getAddressComponent('sublocality'),
            state: getAddressComponent('administrative_area_level_1'),
            postalCode: getAddressComponent('postal_code'),
            country: getAddressComponent('country'),
            countryISO: getAddressComponent('country', true)
          });
        }
      });
    } else if (!newPlace) {
      setPlace(null);
    }
  }, [selectedItems[0]]);
  useEffect(() => {
    if (isLoaded && placesService.current) {
      if (debouncedInputValue.trim() !== '') {
        autocompleteService.current.getPlacePredictions({
          input: debouncedInputValue,
          types: props.placeTypes || ['(cities)']
        }, (predictions, status_0) => {
          if (status_0 === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setLocationPredictions(predictions.map(prediction => ({
              description: prediction.description,
              placeId: prediction.place_id
            })));
          } else {
            setLocationPredictions([]);
          }
        });
      }
    }
  }, [debouncedInputValue]);
  if (!isLoaded) {
    return <Skeleton borderRadius={'0.25em'} height={'2.5em'} width={'full'} css={{
      '--end-color': 'blackAlpha.500',
      '--start-color': 'blackAlpha.300'
    }} />;
  }
  return <Box width={'full'}>
      <div ref={placesRef} />
      <ComboboxTagInput {...props.comboboxTagInputProps || {}} allowMultipleSelections={false} defaultSelectedItems={props.defaultValue ? [{
      key: 'default',
      ...props.defaultValue
    }] : []} hideItemsOnBlank={true} icon={LuMapPin} itemsAreFiltered={true} itemsAreLoading={predictionsLoading} placeholder={props.placeholder || 'Search locations...'} renderSelectedItems={false} items={locationPredictions.map(prediction_0 => ({
      key: prediction_0.placeId,
      title: prediction_0.description
    }))} onClear={props.onClear} onChange={selectedItems_0 => {
      setSelectedItems(selectedItems_0);
    }} onInputChange={value => {
      setInputValue(value);
    }} />
    </Box>;
};